import React from 'react';
import * as FiIcons from 'react-icons/fi';
import useEventStream from '../../hooks/useEventStream';
import StatusDot from '../../components/status/StatusDot.jsx';
import DataModeBadge from '../../components/feedback/DataModeBadge.jsx';
import EventFeedItem from './EventFeedItem';

const MAX_EVENTS = 30;

function toSortTime(event) {
  if (event?.timestamp instanceof Date) return event.timestamp.getTime();
  if (typeof event?.timestamp === 'number') return event.timestamp;
  if (typeof event?.createdAt === 'number') return event.createdAt;
  return 0;
}

/**
 * Right-rail live-activity timeline (T2a). Events default to the
 * useEventStream hook (realtime slice integration point) and can be
 * overridden via the `events` prop. Newest first, capped at 30 with an
 * overflow count footer.
 */
function EventFeed({ events: eventsProp = null, max = MAX_EVENTS }) {
  const stream = useEventStream();
  const source = Array.isArray(eventsProp) ? eventsProp : stream.events;

  const events = [...source].sort((a, b) => toSortTime(b) - toSortTime(a));
  const visible = events.slice(0, max);
  const overflow = Math.max(events.length - visible.length, 0);

  return (
    <aside className="event-panel">
      <div className="section-heading">
        <div>
          <h2>
            Live activity <DataModeBadge mode="sample" label="Sample events — realtime pending" />
          </h2>
          {/* Cutover §3: no event stream exists yet — the feed never claims
              to stream. "Reconnecting…" was a lie over a mock source; the
              pulsing StatusDot is stream-state only (idle = no pulse). */}
          <span>{stream.isConnected ? 'Streaming now' : 'Not streaming — showing sample events'}</span>
        </div>
        <StatusDot pulse={stream.isConnected} />
      </div>
      <div className="event-list">
        {visible.length === 0 ? (
          <p className="event-empty">No events yet — the stream is quiet.</p>
        ) : (
          visible.map((event, index) => (
            <EventFeedItem
              event={event}
              key={event.id ?? `${event.agent || 'event'}-${index}`}
            />
          ))
        )}
      </div>
      {overflow > 0 ? (
        <button className="event-footer" type="button">
          +{overflow} earlier events <FiIcons.FiArrowRight />
        </button>
      ) : null}
    </aside>
  );
}

export default EventFeed;
